import {
  AfterViewInit,
  Component,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSelect } from '@angular/material/select';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute } from '@angular/router';
import moment from 'moment';
import { forkJoin, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AventuraService } from 'src/app/services/api/aventura.service';

export type FilterField =
  | 'proveedor'
  | 'categoria'
  | 'comunidad'
  | 'file'
  | 'ruta';

export interface FilterSearchQueries {
  proveedor: string;
  categoria: string;
  comunidad: string;
  file: string;
  ruta: string;
}

export interface FilteredOptionsMap {
  proveedor: string[];
  categoria: string[];
  comunidad: string[];
  file: string[];
  ruta: string[];
}

export interface ProviderContactos {
  contactoCelular?: string;
  contactoCorreo?: string;
}

export interface PersonalCampoInfo {
  comunidad?: string;
}

export interface FechasOcupadasDetalle {
  fechaInicio?: string;
  fechaFin?: string;
  itineraryId?: string;
  productId?: string;
  productName?: string;
  fileNumber?: string;
  categoriaServicio?: string;
}

export interface SourcingProvider {
  id?: string;
  locacion?: string;
  tipoProveedor?: string;
  nombre?: string;
  categoriaProveedor?: string;
  direccionLegal?: string;
  fechasOcupadas?: FechasOcupadasDetalle[];
  personalCampoInfo?: PersonalCampoInfo;
  contactos?: ProviderContactos;
}

export interface ReportePersonalRow {
  fechasOcupadas: FechasOcupadasDetalle;
  proveedor: SourcingProvider;
  contactos?: ProviderContactos;
}

@Component({
  selector: 'app-reporte-personal-campo',
  templateUrl: './reporte-personal-campo.component.html',
  styleUrls: ['./reporte-personal-campo.component.css']
})
export class ReportePersonalCampoComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  loading: boolean = true;
  dataSource: MatTableDataSource<ReportePersonalRow> =
    new MatTableDataSource<ReportePersonalRow>();
  sourcingProviders: SourcingProvider[] = [];

  fechasOcupadas: ReportePersonalRow[] = [];

  displayedColumns: string[] = [
    'proveedor',
    'comunidad',
    'categoria',
    'correo',
    'telefono',
    'fecha-inicio',
    'fecha-liberacion',
    'file',
    'ruta'
  ];

  selectedFile: string | null = null;
  selectedRuta: string | null = null;
  selectedProveedor: string | null = null;
  selectedComunidad: string | null = null;
  selectedCategoria: string | null = null;

  startDate: Date | null = null;
  endDate: Date | null = null;
  startDateSelected: Date | null = null;
  endDateSelected: Date | null = null;
  location: string | null = null;

  arrayFiles: string[] = [];
  arrayRutas: string[] = [];
  arrayProveedores: string[] = [];
  arrayCategorias: string[] = [];
  arrayComunidades: string[] = ['CAMICANCHA', 'HUILLOC', 'LAMAY', 'SOCMA'];

  searchQueries: FilterSearchQueries = {
    proveedor: '',
    categoria: '',
    comunidad: '',
    file: '',
    ruta: ''
  };

  filteredOptions: FilteredOptionsMap = {
    proveedor: [],
    categoria: [],
    comunidad: [],
    file: [],
    ruta: []
  };

  @ViewChild(MatPaginator) paginator!: MatPaginator;

  private destroy$ = new Subject<void>();

  constructor(
    private aventuraService: AventuraService,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.route.params.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      this.location = params['location'] || null;
      this.getProviders();
    });
  }

  ngAfterViewInit(): void {
    if (this.paginator) {
      this.dataSource.paginator = this.paginator;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getLocation(location?: string | null): string {
    const loc = (location || this.location || '').toLowerCase();
    if (loc === 'cusco') {
      return 'Cusco';
    } else if (loc === 'huaraz') {
      return 'Huaraz';
    }
    return location || this.location || '';
  }

  getProviders(): void {
    this.loading = true;
    const personalDeCampo$ =
      this.aventuraService.getAllProveedoresByType('PERSONALDECAMPO');
    const guia$ = this.aventuraService.getAllProveedoresByType('GUIA');

    forkJoin([personalDeCampo$, guia$])
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          const providersPersonaldecampo: SourcingProvider[] =
            response?.[0]?.getAllProviderByType || [];
          const providersGuia: SourcingProvider[] =
            response?.[1]?.getAllProviderByType || [];

          this.sourcingProviders = [
            ...providersPersonaldecampo,
            ...providersGuia
          ].filter(
            (provider) =>
              Array.isArray(provider?.fechasOcupadas) &&
              provider.fechasOcupadas.length > 0
          );

          this.fechasOcupadas = [];

          for (const provider of this.sourcingProviders) {
            const matchesLocation =
              !this.location ||
              provider.locacion?.toLowerCase() === this.location.toLowerCase();

            if (!matchesLocation) {
              continue;
            }

            for (const occupiedDate of provider.fechasOcupadas || []) {
              this.fechasOcupadas.push({
                fechasOcupadas: occupiedDate,
                proveedor: provider,
                contactos: provider.contactos
              });
            }
          }

          this.arrayFiles = this.getUniqueOptions(
            this.fechasOcupadas.map((item) => item.fechasOcupadas?.fileNumber),
            'desc'
          );
          this.arrayRutas = this.getUniqueOptions(
            this.fechasOcupadas.map((item) => item.fechasOcupadas?.productName)
          );
          this.arrayCategorias = this.getUniqueOptions(
            this.fechasOcupadas.map(
              (item) => item.fechasOcupadas?.categoriaServicio
            )
          );
          this.arrayProveedores = this.getUniqueOptions(
            this.fechasOcupadas.map((item) => item.proveedor?.nombre)
          );

          const dynamicComunidades = this.getUniqueOptions(
            this.fechasOcupadas.map(
              (item) => item.proveedor?.personalCampoInfo?.comunidad
            )
          );
          this.arrayComunidades = this.sortAscending(
            Array.from(
              new Set([...this.arrayComunidades, ...dynamicComunidades])
            )
          );

          this.resetAllFilteredOptions();

          this.dataSource.data = this.fechasOcupadas;
          if (this.paginator) {
            this.dataSource.paginator = this.paginator;
          }
          this.loading = false;
        },
        error: (error) => {
          console.error('Error al cargar proveedores para reporte:', error);
          this.loading = false;
        }
      });
  }

  getSourceArray(type: FilterField): string[] {
    switch (type) {
      case 'proveedor':
        return this.arrayProveedores;
      case 'categoria':
        return this.arrayCategorias;
      case 'comunidad':
        return this.arrayComunidades;
      case 'file':
        return this.arrayFiles;
      case 'ruta':
        return this.arrayRutas;
    }
  }

  resetAllFilteredOptions(): void {
    this.filteredOptions = {
      proveedor: [...this.arrayProveedores],
      categoria: [...this.arrayCategorias],
      comunidad: [...this.arrayComunidades],
      file: [...this.arrayFiles],
      ruta: [...this.arrayRutas]
    };
  }

  onSearchFilter(type: FilterField, text: string): void {
    this.searchQueries[type] = text;
    const query = this.normalizeText(text);
    const source = this.getSourceArray(type);

    if (!query) {
      this.filteredOptions[type] = [...source];
    } else {
      this.filteredOptions[type] = source.filter((item) =>
        this.normalizeText(item).includes(query)
      );
    }
  }

  clearSearchQuery(type: FilterField, event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
    }
    this.searchQueries[type] = '';
    this.filteredOptions[type] = [...this.getSourceArray(type)];
  }

  onDropdownOpened(isOpen: boolean, type: FilterField): void {
    if (isOpen) {
      setTimeout(() => {
        const panel = document.querySelector('.custom-filter-select-panel');
        if (panel) {
          panel.scrollTop = 0;
        }
        const input = document.getElementById(
          `select-search-${type}`
        ) as HTMLInputElement;
        if (input) {
          input.focus();
        }
      }, 100);
    } else {
      this.clearSearchQuery(type);
    }
  }

  onSearchKeydown(
    event: KeyboardEvent,
    type: FilterField,
    selectRef: MatSelect
  ): void {
    if (event.key === 'Escape') {
      selectRef.close();
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const options = this.filteredOptions[type];
      if (options && options.length === 1) {
        this.selectOption(type, options[0]);
        selectRef.close();
      }
    } else {
      event.stopPropagation();
    }
  }

  selectOption(type: FilterField, value: string | null): void {
    switch (type) {
      case 'proveedor':
        this.selectedProveedor = value;
        break;
      case 'categoria':
        this.selectedCategoria = value;
        break;
      case 'comunidad':
        this.selectedComunidad = value;
        break;
      case 'file':
        this.selectedFile = value;
        break;
      case 'ruta':
        this.selectedRuta = value;
        break;
    }
    this.applyFilterSelect();
  }

  clearSelectedFilter(type: FilterField, event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
    }
    this.selectOption(type, null);
  }

  clearAllFilters(): void {
    this.selectedProveedor = null;
    this.selectedCategoria = null;
    this.selectedComunidad = null;
    this.selectedFile = null;
    this.selectedRuta = null;
    this.startDate = null;
    this.endDate = null;
    this.startDateSelected = null;
    this.endDateSelected = null;

    for (const key of Object.keys(this.searchQueries) as FilterField[]) {
      this.clearSearchQuery(key);
    }

    this.applyFilterSelect();
  }

  get hasActiveFilters(): boolean {
    return Boolean(
      this.selectedProveedor ||
      this.selectedCategoria ||
      this.selectedComunidad ||
      this.selectedFile ||
      this.selectedRuta ||
      this.startDateSelected ||
      this.endDateSelected
    );
  }

  applyFilterSelect(): void {
    let filteredData = this.fechasOcupadas;

    if (
      this.selectedFile ||
      this.selectedCategoria ||
      this.selectedRuta ||
      this.selectedProveedor ||
      this.selectedComunidad ||
      this.startDateSelected ||
      this.endDateSelected
    ) {
      filteredData = this.fechasOcupadas.filter((item) => {
        if (
          this.selectedFile &&
          item.fechasOcupadas?.fileNumber !== this.selectedFile
        ) {
          return false;
        }
        if (
          this.selectedCategoria &&
          item.fechasOcupadas?.categoriaServicio !== this.selectedCategoria
        ) {
          return false;
        }
        if (
          this.selectedRuta &&
          item.fechasOcupadas?.productName !== this.selectedRuta
        ) {
          return false;
        }
        if (
          this.selectedProveedor &&
          item.proveedor?.nombre !== this.selectedProveedor
        ) {
          return false;
        }
        if (
          this.selectedComunidad &&
          item.proveedor?.personalCampoInfo?.comunidad !==
            this.selectedComunidad
        ) {
          return false;
        }
        if (this.startDateSelected || this.endDateSelected) {
          return this.matchesDateFilter(item.fechasOcupadas);
        }
        return true;
      });
    }

    this.dataSource.data = filteredData;
    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  onApplyRangeSelected(): void {
    if (!this.startDate && !this.endDate) {
      return;
    }

    const start = this.startDate ? new Date(this.startDate) : null;
    const end = this.endDate ? new Date(this.endDate) : null;
    if (end) {
      end.setHours(23, 59, 59, 999);
    }

    this.startDateSelected = start;
    this.endDateSelected = end;
    this.applyFilterSelect();
  }

  cleanDateSelected(): void {
    this.startDate = null;
    this.endDate = null;
    this.startDateSelected = null;
    this.endDateSelected = null;

    this.applyFilterSelect();
  }

  private parseDate(dateStr?: string): number | null {
    if (!dateStr) {
      return null;
    }
    const m = moment(dateStr, 'DD/MM/YYYY');
    return m.isValid() ? m.valueOf() : null;
  }

  private matchesDateFilter(fechas?: FechasOcupadasDetalle): boolean {
    if (!fechas) {
      return false;
    }

    const itemStart = this.parseDate(fechas.fechaInicio);
    const itemEnd = this.parseDate(fechas.fechaFin);

    if (!itemStart && !itemEnd) {
      return false;
    }

    const start = itemStart ?? itemEnd!;
    const end = itemEnd ?? itemStart!;

    if (this.startDateSelected && this.endDateSelected) {
      return (
        start <= this.endDateSelected.getTime() &&
        end >= this.startDateSelected.getTime()
      );
    }
    if (this.startDateSelected) {
      return end >= this.startDateSelected.getTime();
    }
    if (this.endDateSelected) {
      return start <= this.endDateSelected.getTime();
    }
    return true;
  }

  private normalizeText(text: string): string {
    return (text || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();
  }

  private sortAscending(list: string[]): string[] {
    return list.sort((a, b) =>
      a.localeCompare(b, 'es', { numeric: true, sensitivity: 'base' })
    );
  }

  private sortDescending(list: string[]): string[] {
    return list.sort((a, b) =>
      b.localeCompare(a, 'es', { numeric: true, sensitivity: 'base' })
    );
  }

  private getUniqueOptions(
    values: Array<string | undefined | null>,
    order: 'asc' | 'desc' = 'asc'
  ): string[] {
    const valid = values.filter((val): val is string =>
      Boolean(val && val.trim())
    );
    const unique = Array.from(new Set(valid));
    return order === 'desc'
      ? this.sortDescending(unique)
      : this.sortAscending(unique);
  }
}
